import { getApiErrorMessage } from '../../utils/apiError';
import { mapTransportMutationErrorToMessage } from './transportRouteViews';

/**
 * Single home for turning a transport API failure into a Polish sentence.
 * Sibling of `modules/warehouse/pzErrorMessages.ts` and `wzErrorMessages.ts`.
 *
 * Five pages used to repeat the same three-way chain by hand:
 *   mapTransportMutationErrorToMessage(...) || t(genericKey)
 * — and each picked its own generic key, several of which said only "Błąd
 * podczas ...". This wraps the chain once so every transport screen reports the
 * same cause the same way, and so the generic tail can name the transport.
 *
 * `mapTransportMutationErrorToMessage` (transportRouteViews.tsx) stays the home
 * of the code→sentence decisions themselves, including the WZ/PZ link guards
 * that interpolate blocking document numbers.
 */

/**
 * Failures the operator cannot retry away: something else in the system (a WZ,
 * a PZ, a vanished order link) has to be changed first. Their toasts stay on
 * screen until dismissed.
 */
const TRANSPORT_BLOCKING_ERROR_CODES = new Set([
  'SUPPLIER_ORDER_DIRECT_DELIVERY_REQUIRES_ORIGIN',
  'SUPPLIER_ORDER_DIRECT_DELIVERY_REQUIRES_PAIRING',
  'WZ_TRANSPORT_LINK_EXISTS',
  'PZ_TRANSPORT_LINK_EXISTS',
  'TRANSPORT_HAS_NO_ORDERS',
  'CARGO_OVER_ALLOCATION',
  'TRANSPORT_CARGO_REQUIRED',
  'TRANSPORT_CARGO_INVALID',
  'TRANSPORT_SUPPLIER_ORDER_CARGO_UNMAPPED',
  'SUPPLIER_CARGO_LINE_NOT_ON_TRUCK',
  'TRANSPORT_CARGO_PAIRING_LINE_NOT_ON_TRUCK',
  'ORDER_FINANCIAL_LIMIT_EXCEEDED',
  'TRANSPORT_SETTLEMENT_BASIS_LOCKED',
  'TRANSPORT_EXCHANGE_RATE_CONFLICT',
  'TRANSPORT_ASSIGNMENTS_CHANGED',
  'TRANSPORT_ACTUAL_DELIVERY_REQUIRED',
  'TRANSPORT_DELIVERY_QUANTITIES_REQUIRED',
  'TRANSPORT_DELIVERY_QUANTITIES_INVALID',
  'TRANSPORT_DELIVERY_LAST_LINE_REMOVED',
]);

/**
 * Code → i18n key map for the transport `blockedActions` alert/tooltips. The
 * toast wording for the same codes lives in `mapTransportMutationErrorToMessage`.
 */
export const TRANSPORT_ERROR_CODE_KEYS: Record<string, string> = {
  TRANSPORT_SETTLEMENT_BASIS_LOCKED: 'transport:messages.settlementBasisLocked',
  WZ_TRANSPORT_LINK_EXISTS: 'transport:messages.wzLinkExists',
  PZ_TRANSPORT_LINK_EXISTS: 'transport:messages.pzLinkExists',
};

export function isBlockingTransportError(error: any): boolean {
  const code = error?.response?.data?.code;
  return typeof code === 'string' && TRANSPORT_BLOCKING_ERROR_CODES.has(code);
}

/**
 * @param fallbackKey i18n key used when the server says nothing specific. Must
 *   name the failed operation, e.g. `transport:errorUpdatingRoute`.
 * @param fallbackParams interpolation for the fallback key — pass `{ number }`
 *   so even the generic path identifies the transport.
 */
export function getTransportErrorMessage(
  error: any,
  t: any,
  fallbackKey: string,
  fallbackParams?: Record<string, unknown>
): string {
  const mapped = mapTransportMutationErrorToMessage(t, error?.response?.data);
  if (mapped) return mapped;
  return getApiErrorMessage(error, t, fallbackKey, undefined, fallbackParams);
}
